import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';

export default function CheckoutSuccessView() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get('session_id');

  const [state, setState] = useState('loading'); // loading | success | error
  const [license, setLicense] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const fetchResult = async () => {
      try {
        const getCheckoutResult = httpsCallable(functions, 'getCheckoutResult');
        const result = await getCheckoutResult({ sessionId });
        setLicense(result.data);
        setState('success');
      } catch (err) {
        console.error('[CheckoutSuccess] Failed to fetch result:', err);
        setState('error');
      }
    };
    fetchResult();
  }, [sessionId]);

  const handleCopy = () => {
    if (!license?.licenseKey) return;
    navigator.clipboard.writeText(license.licenseKey).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const planLabel = license?.type === 'yearly' ? 'Tahunan' : 'Bulanan';
  const expiryDate = license?.expiresAt
    ? new Date(license.expiresAt).toLocaleDateString('ms-MY', { day: 'numeric', month: 'long', year: 'numeric' })
    : null;

  if (state === 'loading') {
    return (
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="text-center">
          <div className="w-10 h-10 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-400 text-sm">Mengesahkan pembayaran...</p>
        </div>
      </div>
    );
  }

  if (state === 'error') {
    return (
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="text-center max-w-sm">
          <div className="w-16 h-16 rounded-2xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center mx-auto mb-4">
            <span className="material-symbols-outlined text-amber-500 text-3xl">warning</span>
          </div>
          <h2 className="text-xl font-bold text-slate-800 dark:text-white mb-2">Pembayaran Diterima</h2>
          <p className="text-slate-500 dark:text-slate-400 mb-2 text-sm">
            Akaun anda sedang diproses. Jika masalah berterusan, semak email anda untuk kunci lesen.
          </p>
          <div className="flex gap-3 justify-center mt-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="px-5 py-2 bg-emerald-500 text-white rounded-xl font-medium hover:bg-emerald-600 transition-colors text-sm"
            >
              Ke Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex items-center justify-center p-8">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 rounded-2xl bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mx-auto mb-4">
            <span className="material-symbols-outlined text-emerald-500 text-4xl">check_circle</span>
          </div>
          <h1 className="text-2xl font-bold text-slate-800 dark:text-white mb-1">Pembayaran Berjaya!</h1>
          <p className="text-slate-500 dark:text-slate-400 text-sm">Selamat datang ke SESAAT.AI Premium 🎉</p>
        </div>

        {/* License Key Card */}
        <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6 mb-5">
          <p className="text-xs text-slate-400 uppercase tracking-widest mb-2">Kunci Lesen Anda</p>
          <div className="flex items-center gap-3">
            <code className="flex-1 text-emerald-400 font-bold text-lg tracking-widest break-all">
              {license?.licenseKey}
            </code>
            <button
              onClick={handleCopy}
              className="shrink-0 w-9 h-9 flex items-center justify-center rounded-xl bg-slate-700 hover:bg-slate-600 transition-colors"
              title="Salin"
            >
              <span className="material-symbols-outlined text-slate-300 text-lg">
                {copied ? 'check' : 'content_copy'}
              </span>
            </button>
          </div>
          {copied && <p className="text-xs text-emerald-400 mt-2">Disalin!</p>}
        </div>

        {/* Plan Info */}
        <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4 mb-6 grid grid-cols-2 gap-3 text-sm">
          <div>
            <p className="text-slate-500 text-xs mb-0.5">Plan</p>
            <p className="text-slate-200 font-medium">Premium {planLabel}</p>
          </div>
          <div>
            <p className="text-slate-500 text-xs mb-0.5">Tamat Pada</p>
            <p className="text-slate-200 font-medium">{expiryDate ?? '—'}</p>
          </div>
        </div>

        {/* Info note */}
        <p className="text-xs text-slate-500 text-center mb-6">
          Kunci lesen telah dihantar ke email anda. Lesen diaktifkan secara automatik.
        </p>

        {/* CTAs */}
        <div className="flex flex-col gap-3">
          <button
            onClick={() => navigate('/dashboard')}
            className="w-full py-3 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl font-semibold transition-colors"
          >
            Buka Dashboard
          </button>
          <button
            onClick={() => navigate('/affiliate')}
            className="w-full py-3 bg-slate-700 hover:bg-slate-600 text-slate-200 rounded-xl font-medium transition-colors text-sm"
          >
            Program Affiliate — Jana Pendapatan
          </button>
        </div>
      </div>
    </div>
  );
}
